import React, { useState } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { FastAvatar } from '@/Components/FastAvatar';
import { useUsers } from '@/Recoil/User';
import { useLikes, useBookmarks } from '@/Recoil/Social';
import { navigateTo } from '@/Utils/routing';
import type { Post } from '@/Data/Social';
import './styles/PostThin.scss';

interface PostThinProps {
    post: Post;
    isLast?: boolean;
    disableNavigation?: boolean;
}

const PostThin: React.FC<PostThinProps> = ({ post, isLast = false, disableNavigation = false }) => {
    const { profiles, syncProfiles } = useUsers();
    const { isPostLiked, likePost, unlikePost } = useLikes();
    const { bookmarkPost, unbookmarkPost, postBookmarks } = useBookmarks();
    const [isExpanded, setIsExpanded] = useState(false);

    const userProfile = profiles[post.addressid];
    const liked = isPostLiked(post.postid);
    const bookmarked = postBookmarks[post.postid]?.userBookmarked ?? false;

    React.useEffect(() => {
        if (!userProfile) { syncProfiles([post.addressid]); }
    }, [post.addressid, userProfile, syncProfiles]);

    const handleLike = async () => {
        try {
            if (liked) { await unlikePost(post.postid); }
            else { await likePost(post.postid); }
        } catch (error) {
            console.error('Error toggling like:', error);
        }
    };

    const handleBookmark = async () => {
        try {
            if (bookmarked) { await unbookmarkPost(post.postid); }
            else { await bookmarkPost(post.postid); }
        } catch (error) {
            console.error('Error toggling bookmark:', error);
        }
    };

    const contentWithoutHashtags = post.content.replace(/#\w+/g, '').trim();
    const hasImage = post.media && post.media.length > 0;

    const handlePostClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.InteractionButton')) { return; }

        if (!isExpanded) {
            setIsExpanded(true);
        } else if (!disableNavigation) {
            navigateTo(`/social/post/${post.postid}`);
        }
    };

    return (
        <div className={`PostThin ${isLast ? 'IsLast' : ''} ${isExpanded ? 'Expanded' : ''}`} onClick={handlePostClick}>
            <div className="VerticalLineLower" />
            <div className="ProfileImage">
                <FastAvatar uri={userProfile?.image} userid={post.addressid} size={24} style={{ borderRadius: '50%' }} />
            </div>
            <div className="Content">
                <div className="UserInfo">
                    <span className="UserName">{userProfile?.name || post.addressid}</span>
                    <span className="UserCode">@{post.addressid}</span>
                </div>

                <div className={"PostContent"}> 
                    <div className={"TextSection"}>
                        <div className="Text">
                            {contentWithoutHashtags}
                        </div>
                        <div className="TranslationText">
                            {post.translated ?? "Translation will be ready in a few minutes"}
                        </div>
                    </div>
                    {hasImage && <div className="WithImage">
                        {post.media.slice(0,3).map(media =>
                            <div key={media} className="Image" style={{ backgroundImage: `url(${media})` }} onClick={(e)=>{
                                e.stopPropagation();
                                navigateTo(`/social/image/${post.postid}/0`)
                            }} />
                        )}
                    </div>}
                </div>

                <div className="InteractionBar">
                    <button className="InteractionButton">
                        <ChatBubbleOutlineIcon />
                        <span className="Count">{post.reply_count || 0}</span>
                    </button>
                    <button className={`InteractionButton ${liked ? 'Active' : ''}`} onClick={handleLike}>
                        {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                        <span className="Count">{post.like_count || 0}</span>
                    </button>
                    <button className="InteractionButton">
                        <VisibilityIcon />
                        <span className="Count">{post.view_count || 0}</span>
                    </button>
                    <button className={`InteractionButton ${bookmarked ? 'Active' : ''}`} onClick={handleBookmark}>
                        {bookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PostThin;
